// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import axios from 'axios';

// Base de l'API : celle donnée à la construction (REACT_APP_API_URL), sinon
// `/api` sur la même origine.
export const API_URL = process.env.REACT_APP_API_URL || '/api';

// Clé du jeton de session dans localStorage. La console des contrats
// (public/contrats.html), qui n'a pas d'écran de connexion, lit la même clé
// sous le même nom.
export const SESSION_KEY = 'access_token';

// Client HTTP de tous les écrans authentifiés.
const api = axios.create({
  baseURL: API_URL,
});

// Jeton posé sur chaque requête.
api.interceptors.request.use((config) => {
  const token = localStorage.getItem(SESSION_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Sur 401, la session est effacée et l'utilisateur renvoyé à la connexion. Sauf
// pour la connexion elle-même : son 401 veut dire « mauvais identifiants », et
// recharger la page effacerait le message de l'écran de connexion.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const url = String(error.config?.url || '');
    if (error.response?.status === 401 && !url.endsWith('/auth/login')) {
      localStorage.removeItem(SESSION_KEY);
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Déconnexion côté serveur. POST /api/auth/logout incrémente la version de
// jeton du compte : ce jeton et ceux des autres postes du compte sont refusés
// dès la requête suivante, alors qu'effacer localStorage seul le laisserait
// valable 8 h. À appeler avant d'effacer la session locale, avec le jeton
// courant. `fetch` direct : l'intercepteur 401 d'axios renverrait vers /login
// en pleine déconnexion. Jamais bloquante : panne réseau, 401 ou lenteur
// au-delà de LOGOUT_DELAY_MS ne retiennent pas l'utilisateur. Rend vrai si le
// serveur a confirmé.
export const LOGOUT_DELAY_MS = 4000;
export async function revokeSession(currentToken, delayMs = LOGOUT_DELAY_MS) {
  if (!currentToken) return false;
  const abandon = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = abandon ? setTimeout(() => abandon.abort(), delayMs) : null;
  try {
    const reply = await fetch(`${API_URL}/auth/logout`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${currentToken}` },
      ...(abandon ? { signal: abandon.signal } : {}),
    });
    return reply.ok;
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

export default api;
